import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { UserManagementService } from 'src/app/services/user-management.service';
import { environment } from 'src/environments/environment';
import { ErrorHandlerDirective } from '../directives/error-handler.directive';
import { IApiResponse, MethodTypes } from '../interface/interface';

@Injectable({
  providedIn: 'root',
})

/**
 * Common helper functions
 */
export class CommonHelper {
  public marketPlaceUrl = environment.MARKETPLACE_URL;

  constructor(private userManagementService: UserManagementService, private errorHandler: ErrorHandlerDirective) { }
  /**
   * File Download
   * @param {string} path
   * @param {string} fileName
   */
  public fileDownload(path: string, fileName: string) {
    const link = document.createElement('a');
    link.setAttribute('target', '_blank');
    link.setAttribute('href', environment.FILE_PATH + path);
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  /**
   *
   * @param data
   * @returns
   */
  public decimalToHex(data: number | string) {
    let hex = Number(data).toString(16);
    while (hex.length < 2) {
      hex = '0' + hex;
    }
    return '0x' + hex;
  }

  async checkAdminKycStatus(): Promise<boolean> {
    try {
      const response: IApiResponse = await firstValueFrom(this.userManagementService.getAdminKycStatus());
      return response['data']['admin_kyc_status'];
    } catch (error) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
   * Gets the URL for the NFT detail page based on the input.
   * Input can be:
   * - A logsDetails-like object (has nft_ids array)
   * - A single NFT object (has token_id)
   * - A wrapper { nft: {...} } for a single NFT object
   * If input is null or undefined, returns an empty string.
   * If input is a wrapper, uses the inner NFT object.
   * If the NFT object has no token_id, returns an empty string.
   * Otherwise, builds the URL based on the NFT object's properties.
   * @param input The input object to get the NFT URL for.
   * @returns The URL for the NFT detail page.
   */
  getNftUrl(input: any): string {
    if (!input) return '';
  
    // 1) If input is logsDetails-like (has nft_ids array) -> take first element
    let candidate = null;
    if (input?.nft_ids && Array.isArray(input.nft_ids)) {
      candidate = input.nft_ids[0];
    } else {
      candidate = input;
    }
  
    // 2) If candidate is a wrapper { nft: {...} } use candidate.nft, else use candidate itself
    const nft = candidate?.nft ?? candidate;
  
    // 3) Validate token_id existence (allow token_id === 0)
    if (nft == null) return '';
    if (nft.token_id === undefined || nft.token_id === null) return '';
  
    // 4) Build path (safe access for collection address)
    const baseUrl = environment.MARKETPLACE_URL || '';
    const collectionAddr = nft.collections?.collection_address ?? nft.collection_address ?? '';
    const path = nft.lazy_mint
      ? `lazy-mint/${nft._id}`
      : `nft-detail/${collectionAddr}/${nft.token_id}`;
  
    return `${baseUrl.replace(/\/?$/, '/')}${path}`; // ensure single trailing slash
  }
  


 clickRedirect(data: any, callBack: (route: string) => any) {
  // loan_request_id / collection_address are now returned as plain fields on the row itself
  // (backend derives them at write time) instead of requiring `data.data` to be parsed —
  // the transaction list no longer sends `data` at all.
  const dataValue = data;
  let redirect_url = '';

  const loanDetailMethods = [
    MethodTypes.LOAN_DEFAULT,
    MethodTypes.REQ_LOAN,
    MethodTypes.UPDATE_LOAN,
    MethodTypes.FORECLOSE_LOAN,
    MethodTypes.LOAN_REPAID,
    MethodTypes.REQUESTEDREFINANCELOAN,
    MethodTypes.UPDATEDREFINANCELOANREQUEST,
    MethodTypes.CANCELLEDREFINANCELOANREQUEST
  ];

  const offerMethods = [
    MethodTypes.COUNTER_OFFER,
    MethodTypes.RECOUNT_OFFER,
    MethodTypes.BORROW_RECOUNT_OFFER,
  ];

  const nftDetailMethods = [
    MethodTypes.ITEM_MINTED,
    MethodTypes.PURCHASE_SELL_ORDER,
    MethodTypes.CREATE_ITEM_LOAN_BID,
    MethodTypes.BULK_MINT,
    MethodTypes.CREATE_SELL_ORDER,
    MethodTypes.CANCEL_ITEMBID,
    MethodTypes.CREATE_ITEMBID,
    MethodTypes.ACCEPT_LOAN,
    MethodTypes.BURN_ITEM,
    MethodTypes.CANCEL_LOAN,
    MethodTypes.ACCEPT_ITEMBID,
    MethodTypes.PURCHASE_PARTNER_ITEM,
    MethodTypes.ITEM_TRANSFER,
    MethodTypes.CANCEL_SELL_ORDER,
  ];

  const bulkLoanBidMethods = [
    MethodTypes.CANCEL_ITEM_LOAN_BID,
    MethodTypes.COUNTER_OFFER_OPEN_LOAN_BID,
    MethodTypes.CREATE_BULK_LOAN_BID,
    MethodTypes.ACCEPT_BULK_LOAN_BID,
    MethodTypes.CANCEL_BULK_LOAN_BID,
    MethodTypes.EDIT_BULK_LOAN_BID,
    MethodTypes.ACCEPT_ITEM_LOAN_BID,
  ];
  if (MethodTypes.REPAID_INSTALLMENT) {
    const loanId = data?.loan_request_id;
    if (loanId) {
      redirect_url = `${this.marketPlaceUrl}lending-detail/${loanId}`;
    }
  }

   if (MethodTypes.ACCEPTEDREFINANCELOAN) {
    const loanId = data?.loan_request_id;
    if (loanId) {
      redirect_url = `${this.marketPlaceUrl}lending-detail/${loanId}`;
    }
  }

  if (loanDetailMethods.includes(data.method)) {
    const loanId = data?.loan_request_id;
    if (loanId) {
      redirect_url = `${this.marketPlaceUrl}lending-detail/${loanId}`;
    }
  } else
  if (offerMethods.includes(data.method)) {
    const loanId = data?.loan_request_id;
    if (loanId) {
      redirect_url = `${this.marketPlaceUrl}lending-detail/${loanId}`;
    }
  } else if (data.method === MethodTypes.PARTNER_COL_CREATED) {
    const collectionAddress = data?.collection_address;
    if (collectionAddress) {
      redirect_url = `${this.marketPlaceUrl}collection-detail/${collectionAddress}`;
    }
  } else if (nftDetailMethods.includes(data.method)) {
    const collectionAddress = dataValue?.nft_ids?.[0]?.collections?.collection_address;
    const tokenId = dataValue?.nft_ids?.[0]?.token_id;

    if (collectionAddress && tokenId !== undefined) {
      redirect_url = `${this.marketPlaceUrl}nft-detail/${collectionAddress}/${tokenId}`;
    }
  } else if (bulkLoanBidMethods.includes(data.method)) {
    redirect_url = `${this.marketPlaceUrl}lend`;
  } else {
    console.warn(`Unhandled method type: ${data.method}`);
  }

  callBack(redirect_url);
}

}
